/* eslint camelcase: 0 */

import {
  useCallback,
  useState,
  useContext,
} from 'react'
import Decimal from 'decimal.js'
import CheckoutContext from 'contexts/CheckoutContext'
import CustomerPendingsContext from 'contexts/CustomerPendingsContext'
import { requestCustomerPendings, requestRegisterPayment } from 'requests/checkout'
import { requestPaymentMethodsList } from 'requests/payment-methods'
import uniqueId from 'lodash/uniqueId'
import { PAYMENT_PLAN, ORDER_PAYMENT } from 'constants/objects/payment-checkout'
import { decimalForInput } from 'utils/format'

const DEFAULTS_CHANGE_CHECKOUT_VALUE = {
  to_generate_voucher: false,
  to_generate_debt: false,
  change_value: 0,
}

export default function useCheckoutPayment() {
  const { checkoutContext, setCheckoutContext } = useContext(CheckoutContext)
  const {
    customerPendingsContext,
    setCustomerPendingsContext,
  } = useContext(CustomerPendingsContext)

  const [paymentMethods, setPaymentMethods] = useState([])
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([])
  const [finishingPayment, setFinishingPayment] = useState(false)

  const hasCustomer = useCallback(() => {
    const hasId = Boolean(checkoutContext.customerId?.length)
    const hasName = Boolean(checkoutContext.customerName?.length)

    return hasId || hasName
  }, [checkoutContext.customerId, checkoutContext.customerName])

  const getCustomerPendings = useCallback(async (id) => {
    const { data } = await requestCustomerPendings(id)

    const {
      vouchers,
      pending_orders: orders,
      debts,
    } = data.attributes || {}

    setCustomerPendingsContext({
      vouchers,
      orders,
      debts,
    })
  }, [setCustomerPendingsContext])

  const getPaymentMethods = useCallback(async () => {
    const { data, error } = await requestPaymentMethodsList()
    if (error) return

    const valids = data.filter((item) => (
      Boolean(item.attributes?.payment_plans?.data?.length)
    ))

    setPaymentMethods(valids)
    setPaymentMethodOptions(valids.map((item) => ({
      label: item.attributes.name,
      value: item.id,
      planOptions: item.attributes.payment_plans.data.map((plan) => ({
        value: plan.id,
        label: plan.attributes.description,
      })),
    })))
  }, [])

  const sum = (items) => {
    let total = new Decimal(0)

    if (items.length) {
      total = items.reduce((prev, current) => (
        prev.plus(current.value || 0)
      ), new Decimal(0))
    }

    return total
  }

  const updateContext = (params) => {
    const context = {
      ...checkoutContext,
      ...params,
    }

    const totalPaid = sum([...context.paymentPlans, ...context.paymentVouchers])
    const totalToPay = sum(context.paymentItems)

    setCheckoutContext({
      ...context,
      totalPaid,
      totalToPay,
    })
  }

  const createPaymentPlan = () => (
    {
      ...PAYMENT_PLAN,
      payment_plan_options: [],
      selected_method_option: {},
      selected_plan_option: {},
      id: uniqueId('payment-plan-'),
    }
  )

  const validPaymentPlans = () => (
    checkoutContext.paymentPlans.filter((item) => item.value > 0)
  )

  const canAddPaymentPlan = () => {
    const empty = checkoutContext.paymentPlans.filter((item) => (
      !item.selected_method_option?.value
      || !item.selected_plan_option?.value
      || !item.value
      || (item.value && item.value < 0.01)
    ))

    return !empty.length
  }

  const addPaymentPlan = () => {
    if (!canAddPaymentPlan()) return

    const paymentPlans = [...checkoutContext.paymentPlans]
    paymentPlans.push(createPaymentPlan())

    updateContext({
      paymentPlans,
    })
  }

  const updatePaymenMethod = ({ plan, selectedOption }) => {
    const method = paymentMethodOptions.find((item) => (
      item.value === selectedOption.value
    ))

    const plans = [...checkoutContext.paymentPlans]
    const index = plans.findIndex((item) => item.id === plan.id)
    plans[index] = {
      ...plans[index],
      selected_method_option: selectedOption,
      selected_plan_option: method.planOptions[0],
      payment_plan_options: method.planOptions,
    }

    updateContext({
      paymentPlans: plans,
    })
  }

  const updatePlanMethod = ({ plan, selectedOption }) => {
    const plans = [...checkoutContext.paymentPlans]
    const index = plans.findIndex((item) => item.id === plan.id)
    plans[index] = {
      ...plans[index],
      selected_plan_option: selectedOption,
    }

    updateContext({
      paymentPlans: plans,
    })
  }

  const updatePlanValue = ({ plan, value }) => {
    const plans = [...checkoutContext.paymentPlans]
    const index = plans.findIndex((item) => item.id === plan.id)
    plans[index] = {
      ...plans[index],
      value: decimalForInput(value),
    }

    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      paymentPlans: plans,
    })
  }

  const removePaymentPlan = (plan) => {
    const paymentPlans = [...checkoutContext.paymentPlans]

    if (paymentPlans.length < 2) return

    const index = paymentPlans.findIndex((paymentPlan) => (
      paymentPlan.id === plan.id
    ))

    paymentPlans.splice(index, 1)
    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      paymentPlans,
    })
  }

  const voucherIndex = (voucher) => (
    checkoutContext.paymentVouchers.findIndex((item) => item.id === voucher.id)
  )

  const addVoucher = (voucher) => {
    const index = voucherIndex(voucher)
    if (index < 0) {
      const paymentVoucher = {
        id: voucher.id,
        value: voucher.attributes.value,
        code: voucher.attributes.code,
      }

      updateContext({
        paymentVouchers: [...checkoutContext.paymentVouchers, paymentVoucher],
      })
    }
  }

  const removeVoucher = (voucher) => {
    const index = voucherIndex(voucher)
    if (index < 0) return

    const paymentVouchers = [...checkoutContext.paymentVouchers]
    paymentVouchers.splice(index, 1)

    updateContext({
      paymentVouchers,
    })
  }

  const createPaymentItem = (item) => ({
    id: item.id,
    type: item.type,
    value: item.attributes?.value,
    code: item.attributes?.code,
  })

  const balance = () => {
    const { totalPaid, totalToPay } = checkoutContext
    return Decimal(totalPaid).minus(totalToPay)
  }

  const balanceValue = () => balance().toFixed(2)
  const totalValue = () => {
    const total = balanceValue() - checkoutContext.change_value
    return total.toFixed(2)
  }

  const addPaymentItem = (item) => {
    const paymentItems = [...checkoutContext.paymentItems]
    paymentItems.push(createPaymentItem(item))

    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      paymentItems,
    })
  }

  const removePaymentItem = (item) => {
    const paymentItems = [...checkoutContext.paymentItems]
    const index = paymentItems.findIndex((paymentItem) => (
      paymentItem.id === item.id && paymentItem.type === item.type
    ))

    paymentItems.splice(index, 1)

    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      paymentItems,
    })
  }

  const setDebtRegistration = (toGenerate) => {
    updateContext({
      to_generate_debt: toGenerate,
    })
  }

  const setVoucherRegistration = (toGenerate) => {
    updateContext({
      to_generate_voucher: toGenerate,
    })
  }

  const setChangeValue = () => {
    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      change_value: balanceValue(),
    })
  }

  const resetChangeValue = () => {
    updateContext({
      ...DEFAULTS_CHANGE_CHECKOUT_VALUE,
      change_value: 0,
    })
  }

  const initializePayment = ({ customerId, customerName, item }) => {
    updateContext({
      ...ORDER_PAYMENT,
      customerId,
      customerName,
      paymentItems: [createPaymentItem(item)],
      paymentPlans: [createPaymentPlan()],
      paymentVouchers: [],
    })
  }

  const positivePayment = () => {
    const value = balance()
    return !value.isZero() && value.isPositive()
  }

  const negativePayment = () => balance().isNegative()

  const noChangePayment = () => balance().isZero()

  const finishPayment = async () => {
    setFinishingPayment(true)

    const {
      to_generate_voucher,
      to_generate_debt,
      customerId: customer_id,
      change_value,
      paymentItems,
      paymentVouchers,
    } = checkoutContext

    const methods = validPaymentPlans().map((item) => ({
      id: Number(item.selected_plan_option.value),
      type: 'payment_plan',
      value: item.value,
      properties: item.properties,
    }))

    paymentVouchers.forEach((item) => {
      methods.push({
        id: Number(item.id),
        type: 'voucher',
      })
    })

    const items = paymentItems.map((item) => ({
      id: Number(item.id),
      type: item.type,
    }))

    const params = {
      order_payment: {
        to_generate_debt,
        to_generate_voucher,
        customer_id,
        change_value,
        payment_items: items,
        payment_methods: methods,
      },
    }

    const response = await requestRegisterPayment(params)
    setFinishingPayment(false)
    return response
  }

  return [
    {
      ...customerPendingsContext,
      ...checkoutContext,
      paymentMethods,
      paymentMethodOptions,
      finishingPayment,
    },
    {
      getPaymentMethods,
      getCustomerPendings,
      addPaymentPlan,
      addPaymentItem,
      removePaymentPlan,
      removePaymentItem,
      initializePayment,
      updatePaymenMethod,
      updatePlanMethod,
      updatePlanValue,
      canAddPaymentPlan,
      finishPayment,
      negativePayment,
      positivePayment,
      noChangePayment,
      balanceValue,
      setVoucherRegistration,
      setDebtRegistration,
      setChangeValue,
      resetChangeValue,
      addVoucher,
      removeVoucher,
      hasCustomer,
      validPaymentPlans,
      totalValue,
    },
  ]
}
